import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { useAuthentificationStore } from './AuthenticationStore'
import { refreshTokens } from '../services/TokenRefresh'
import { openReauthPopup } from '../services/ReauthService'
import { updateSocketAccessToken } from '../services/SocketAuthService'
import { saveTokens } from '../services/TokenService'
import { KEY_USER_ID } from '../utils/constantes'

export const SESSION_STATUS_AUTHENTICATED = 'authenticated'
export const SESSION_STATUS_CHECKING = 'checking'
export const SESSION_STATUS_EXPIRED = 'expired'
export const SESSION_STATUS_REAUTHENTICATING = 'reauthenticating'

const saveReauthenticatedUser = (userData) => {
  if (!userData) return

  useAuthentificationStore.getState().setUser(userData)
  if (userData.tokenObj) {
    saveTokens(userData.tokenObj)
    updateSocketAccessToken(userData.tokenObj.access_token)
  }
  if (userData._id) {
    localStorage.setItem(KEY_USER_ID, userData._id)
  }
}

export const useSessionStore = create(
  immer((set, get) => ({
    status: SESSION_STATUS_AUTHENTICATED,
    lastError: null,
    pendingSave: null,

    setAuthenticated: () =>
      set((state) => {
        state.status = SESSION_STATUS_AUTHENTICATED
        state.lastError = null
      }),

    markExpired: ({ draftKey = null, retry = null, message = null } = {}) =>
      set((state) => {
        state.status = SESSION_STATUS_EXPIRED
        state.lastError = message
        state.pendingSave = draftKey || retry ? { draftKey, retry } : null
      }),

    ensureFreshSessionForSave: async ({ draftKey = null, retry = null } = {}) => {
      set((state) => {
        state.status = SESSION_STATUS_CHECKING
        state.lastError = null
        state.pendingSave = { draftKey, retry }
      })

      let response
      try {
        response = await refreshTokens({ silent: true })
      } catch (error) {
        response = {
          status: 0,
          data: {
            message: error?.message || 'Session impossible à vérifier',
          },
        }
      }
      if (response?.status === 200) {
        const tokenObj = response.data
        saveTokens(tokenObj)
        updateSocketAccessToken(tokenObj?.access_token)
        set((state) => {
          state.status = SESSION_STATUS_AUTHENTICATED
          state.lastError = null
          state.pendingSave = null
        })
        return { ok: true }
      }

      get().markExpired({
        draftKey,
        retry,
        message: response?.data?.message || 'Session expirée',
      })
      return { ok: false }
    },

    startReauthentication: () => {
      const popup = openReauthPopup()
      if (!popup) {
        set((state) => {
          state.status = SESSION_STATUS_EXPIRED
          state.lastError = 'La fenêtre de reconnexion a été bloquée par le navigateur.'
        })
        return false
      }

      popup.focus()
      const popupCheckId = window.setInterval(() => {
        const status = get().status
        if (!popup.closed && status === SESSION_STATUS_REAUTHENTICATING) return

        window.clearInterval(popupCheckId)
        if (popup.closed && status === SESSION_STATUS_REAUTHENTICATING) {
          set((state) => {
            state.status = SESSION_STATUS_EXPIRED
            state.lastError = 'La fenêtre de reconnexion a été fermée.'
          })
        }
      }, 1000)

      set((state) => {
        state.status = SESSION_STATUS_REAUTHENTICATING
        state.lastError = null
      })
      return true
    },

    completeReauthentication: async (userData) => {
      saveReauthenticatedUser(userData)
      const pendingSave = get().pendingSave

      set((state) => {
        state.status = SESSION_STATUS_AUTHENTICATED
        state.lastError = null
        state.pendingSave = null
      })

      if (pendingSave?.retry) {
        await pendingSave.retry()
      }
    },
  }))
)
